/**
 * メールの履歴（配送記録 / 運用イベント / 操作履歴 / ログインの履歴）。
 *
 * **以前の冒頭コメントは「1 通ごとの配送ログは出せない」と書いていたが、
 * これは `mail_log_entries`（0040）を足す前の前提で、もう正しくない。**
 * 封筒に書いてあること（差出人・宛先・配送結果）は CP に上がってくる。
 * 4 つのタブが出しているものは全部別物なので、混ぜないこと:
 *
 *   配送記録   `GET /logs/delivery` … `mail_log_entries`。1 通ごとの封筒と結果。
 *              **件名も本文も無い**（CLAUDE.md §7-9 の不変条件）。
 *   できごと   `GET /logs/mail`     … ノード側のセキュリティイベント
 *              （`agent.event.*`）。BAN や認証失敗の観測。
 *   操作の履歴 `GET /logs/audit`    … 自組織の監査ログ。誰が何をしたか。
 *   ログイン   `GET /logs/auth`     … コントロールパネルへのログイン記録。
 *
 * 同じパスに別の意味のデータを流すと画面が黙って空になるので、
 * API 側も 4 本を別々に残してある。
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  CursorPagination,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatDateTime,
  useCursorPagination,
  type DataTableColumn,
  formatCount,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink } from '../lib/nav.js';
import { HelpNote } from '../components/help.js';
import {
  MAIL_EVENT_HELP,
  SEVERITY_LABEL,
  mailEventLabel,
  severityTone,
} from '../lib/mail-events.js';
import type {
  AuditLogEntry,
  AuthLogEntry,
  CursorPage,
  DeliveryLogEntry,
  MailEventLogEntry,
} from '../lib/api-types.js';

export function LogsPage() {
  return (
    <>
      <PageHeader
        title="メールの履歴"
        description="サーバーが検知したできごとと、コントロールパネルへのログイン記録を確認できます。"
      />

      {/*
       * **既定は「メールの配送記録」。** 契約者の問い合わせで最も多いのは
       * 「あの取引先に届いたか」で、この画面に来る理由もほぼそれ。
       */}
      <Tabs defaultValue="delivery">
        <TabsList>
          <TabsTrigger value="delivery">メールの配送記録</TabsTrigger>
          <TabsTrigger value="mail">メールまわりのできごと</TabsTrigger>
          <TabsTrigger value="audit">操作の履歴</TabsTrigger>
          <TabsTrigger value="auth">ログインの履歴</TabsTrigger>
        </TabsList>
        <TabsContent value="delivery">
          <DeliveryLogTab />
        </TabsContent>
        <TabsContent value="mail">
          <MailLogTab />
        </TabsContent>
        <TabsContent value="audit">
          <AuditLogTab />
        </TabsContent>
        <TabsContent value="auth">
          <AuthLogTab />
        </TabsContent>
      </Tabs>
    </>
  );
}

/**
 * 1 通ごとの配送記録（`GET /logs/delivery`）。
 *
 * **レンタルメールサーバーでいちばん多い問い合わせ（「あの取引先に届いたか」）に
 * 契約者が自分で答えるための画面。** API は先に在ったのに呼ぶ画面が無く、
 * すべてサポート起票へ流れていた。
 *
 * 絞り込みは**サーバ側**で行う（`q` は差出人・宛先の部分一致で trgm 索引が
 * 効く）。他のタブのように取得後にフィルタすると、1 ページぶんの中からしか
 * 探せず「検索したのに出てこない」になる。
 */
const DISPOSITION_LABEL: Record<string, { label: string; tone: 'succeeded' | 'failed' | 'pending' }> =
  {
    delivered: { label: '配送完了', tone: 'succeeded' },
    bounced: { label: '不達（バウンス）', tone: 'failed' },
    deferred: { label: '再送待ち', tone: 'pending' },
    rejected: { label: '拒否された', tone: 'failed' },
  };

function DeliveryLogTab() {
  const page = useCursorPagination(50);
  // 入力中に毎打鍵で投げないよう、確定した検索語だけをキーに載せる。
  const [input, setInput] = React.useState('');
  const [q, setQ] = React.useState('');
  const [disposition, setDisposition] = React.useState('all');

  const filter = { q, disposition, cursor: page.cursor, limit: page.limit };
  const list = useQuery({
    queryKey: qk.deliveryLogs(filter),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      if (q) params.set('q', q);
      if (disposition !== 'all') params.set('disposition', disposition);
      return api.get<CursorPage<DeliveryLogEntry>>(`/logs/delivery?${params.toString()}`);
    },
  });

  const rows = list.data?.items ?? [];

  const columns: Array<DataTableColumn<DeliveryLogEntry>> = [
    {
      id: 'at',
      header: '日時',
      width: 'w-44',
      cell: (row) => <span className="tabular text-xs">{formatDateTime(row.occurredAt)}</span>,
    },
    {
      id: 'envelope',
      header: '差出人 / 宛先',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm">
            <span className="text-muted-foreground">{row.direction === 'inbound' ? '受信' : '送信'}</span>
            <span className="ml-2 font-medium">{row.recipient}</span>
          </p>
          <p className="truncate font-mono text-xs text-muted-foreground">{row.sender ?? '—'}</p>
        </div>
      ),
    },
    {
      id: 'detail',
      header: '応答',
      hideBelow: 'lg',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-mono text-xs">
            {[row.smtpCode, row.dsnStatus].filter(Boolean).join(' ') || '—'}
          </p>
          {row.diagnostic ? (
            <p className="truncate text-xs text-muted-foreground" title={row.diagnostic}>
              {row.diagnostic}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'disposition',
      header: '結果',
      width: 'w-36',
      cell: (row) => {
        const d = DISPOSITION_LABEL[row.disposition];
        return (
          <StatusBadge
            status={d?.tone ?? 'pending'}
            label={d?.label ?? row.disposition}
            size="sm"
            dot
          />
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <HelpNote title="件名と本文は記録していません">
        ここに出るのは、差出人・宛先・日時・配送の結果だけです。
        メールの件名や本文はサーバーに保存していないため、この画面では検索できません。
        「送ったのに届かない」の切り分けは、宛先アドレスで検索して結果と応答コードをご確認ください。
      </HelpNote>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(input.trim());
          page.reset();
        }}
      >
        <Field
          label="差出人・宛先で探す"
          htmlFor="dl-q"
          className="min-w-[16rem] flex-1"
          hint="アドレスの一部でも検索できます（例: example.com）。"
        >
          <Input
            id="dl-q"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="torihikisaki@example.com"
          />
        </Field>
        <Field label="結果" className="w-44">
          <Select
            value={disposition}
            onValueChange={(v) => {
              setDisposition(v);
              page.reset();
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべて</SelectItem>
              {Object.entries(DISPOSITION_LABEL).map(([value, d]) => (
                <SelectItem key={value} value={value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Button type="submit" variant="outline">
          検索する
        </Button>
        {q || disposition !== 'all' ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setInput('');
              setQ('');
              setDisposition('all');
              page.reset();
            }}
          >
            条件をクリア
          </Button>
        ) : null}
      </form>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.isError ? '配送記録を取得できませんでした。' : undefined}
        emptyTitle={q ? '該当する記録がありません' : '配送記録がありません'}
        emptyDescription={
          q
            ? 'アドレスの一部だけで検索し直すと見つかることがあります。記録の保持期間を過ぎたものは表示されません。'
            : 'メールの送受信が行われると、1 通ごとの記録がここに出ます。'
        }
        footer={
          <CursorPagination
            nextCursor={list.data?.nextCursor ?? null}
            canGoPrev={page.canGoPrev}
            onNext={() => page.goNext(list.data?.nextCursor ?? null)}
            onPrev={page.goPrev}
            limit={page.limit}
            onLimitChange={page.setLimit}
            currentCount={rows.length}
            loading={list.isFetching}
          />
        }
      />
    </div>
  );
}

/**
 * 組織の操作履歴（`GET /logs/audit`）。
 *
 * **以前は契約者が自分の組織の監査ログを一切見られなかった。**
 * 「誰がメールアドレスを消したか」も「運営者がいつ代理ログインしたか」も
 * 追えず、しかも旧版のこの画面は「上位プランの書き出し機能で取得できます」と
 * 案内していた（API 側にプランゲートは無いので、画面を足すだけで全プランに
 * 開放できる）。代理ログインの統制は「契約者が後から確認できる」ことが
 * 前提なので、ここが無いと片手落ちになる。
 *
 * `before_state` / `after_state` は API が返さない（他の利用者の属性や
 * ノードの内部 ID が混ざるため）。出せるのは「いつ・誰が・何に・何をしたか」だけ。
 */
function AuditLogTab() {
  const page = useCursorPagination(50);
  const [action, setAction] = React.useState('all');

  const filter = { action, cursor: page.cursor, limit: page.limit };
  const list = useQuery({
    queryKey: qk.auditLogs(filter),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      // API は前方一致（`action like 'mailbox.%'`）で受ける。
      if (action !== 'all') params.set('action', action);
      return api.get<CursorPage<AuditLogEntry>>(`/logs/audit?${params.toString()}`);
    },
  });

  const rows = list.data?.items ?? [];

  const columns: Array<DataTableColumn<AuditLogEntry>> = [
    {
      id: 'at',
      header: '日時',
      width: 'w-44',
      cell: (row) => <span className="tabular text-xs">{formatDateTime(row.occurredAt)}</span>,
    },
    {
      id: 'action',
      header: '操作',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{auditActionLabel(row.action)}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{row.action}</p>
        </div>
      ),
    },
    {
      id: 'actor',
      header: '実行者',
      width: 'w-56',
      hideBelow: 'md',
      cell: (row) => (
        <div className="min-w-0 space-y-1">
          <p className="truncate text-sm">
            {row.actor ?? (row.actorType === 'staff' ? '運営スタッフ' : 'システム')}
          </p>
          {row.viaImpersonation ? (
            <Badge variant="warning" size="sm">
              代理ログイン中
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'target',
      header: '対象',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => <span className="text-xs text-muted-foreground">{row.targetType ?? '—'}</span>,
    },
    {
      id: 'result',
      header: '結果',
      width: 'w-24',
      cell: (row) => (
        <StatusBadge
          status={row.success ? 'succeeded' : 'failed'}
          label={row.success ? '成功' : '失敗'}
          size="sm"
          dot
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <HelpNote title="運営スタッフの操作もここに出ます">
        サポート対応のために運営スタッフがお客様の画面を代理で操作した場合、
        その操作には「代理ログイン中」の印が付きます。
        身に覚えのない操作があれば、サポートまでご連絡ください。
      </HelpNote>

      <Field label="操作の種類" className="max-w-[16rem]">
        <Select
          value={action}
          onValueChange={(v) => {
            setAction(v);
            page.reset();
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">すべて</SelectItem>
            {AUDIT_ACTION_FILTERS.map((f) => (
              <SelectItem key={f.prefix} value={f.prefix}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.isError ? '操作の履歴を取得できませんでした。' : undefined}
        emptyTitle="記録がありません"
        emptyDescription="設定の変更などを行うと、ここに履歴が残ります。"
        footer={
          <CursorPagination
            nextCursor={list.data?.nextCursor ?? null}
            canGoPrev={page.canGoPrev}
            onNext={() => page.goNext(list.data?.nextCursor ?? null)}
            onPrev={page.goPrev}
            limit={page.limit}
            onLimitChange={page.setLimit}
            currentCount={rows.length}
            loading={list.isFetching}
          />
        }
      />
    </div>
  );
}

/** 前方一致で絞り込める操作の種類。API は `action like '<prefix>%'` で受ける。 */
const AUDIT_ACTION_FILTERS: Array<{ prefix: string; label: string }> = [
  { prefix: 'mailbox.', label: 'メールアドレス' },
  { prefix: 'domain.', label: 'ドメイン' },
  { prefix: 'alias.', label: '転送・エイリアス' },
  { prefix: 'org.', label: '組織の設定' },
  { prefix: 'member.', label: 'メンバー' },
  { prefix: 'billing.', label: 'ご契約・お支払い' },
  { prefix: 'security.', label: 'セキュリティ' },
  { prefix: 'impersonation.', label: '運営による代理ログイン' },
];

/**
 * 監査ログの `action` → 日本語。
 *
 * **網羅は諦める。** `action` は 200 種類以上あり、増えるたびに画面を直す
 * 運用は続かない。よく出るものだけを訳し、残りは
 * 「名詞 + 動詞」の接尾辞で機械的に日本語化して、識別子も併記する
 * （未知の値は識別子をそのまま出す既存方針と揃える）。
 */
const AUDIT_NOUN_LABEL: Record<string, string> = {
  mailbox: 'メールアドレス',
  domain: 'ドメイン',
  alias: 'エイリアス',
  forwarder: '転送設定',
  org: '組織の設定',
  member: 'メンバー',
  billing: 'ご契約',
  invoice: '請求書',
  security: 'セキュリティ設定',
  impersonation: '代理ログイン',
  auth: 'ログイン',
  user: 'アカウント',
  campaign: 'キャンペーン',
  content: 'コンテンツ',
  token: 'API トークン',
  export: 'データの書き出し',
};

const AUDIT_VERB_LABEL: Record<string, string> = {
  created: 'を作成',
  updated: 'を変更',
  deleted: 'を削除',
  started: 'を開始',
  ended: 'を終了',
  enabled: 'を有効化',
  disabled: 'を無効化',
  suspended: 'を停止',
  resumed: 'を再開',
  appealed: 'の解除を申請',
};

function auditActionLabel(action: string): string {
  const [noun, ...rest] = action.split('.');
  const verb = rest[rest.length - 1] ?? '';
  const nounJa = AUDIT_NOUN_LABEL[noun ?? ''];
  const verbJa = AUDIT_VERB_LABEL[verb];
  if (nounJa && verbJa) return `${nounJa}${verbJa}`;
  if (nounJa) return `${nounJa}の操作`;
  return action;
}

function MailLogTab() {
  const page = useCursorPagination(50);
  const [severity, setSeverity] = React.useState('all');

  const list = useQuery({
    queryKey: qk.mailLogs({ severity, cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<MailEventLogEntry>>(`/logs/mail?${params.toString()}`);
    },
  });

  // 重要度での絞り込みは API に無いので画面側で行う。件数が 1 ページぶんしか
  // 無いのでこれで足りる（API に query を足すなら cursor 実装も要る）。
  const rows = (list.data?.items ?? []).filter(
    (r) => severity === 'all' || r.severity === severity,
  );

  const columns: Array<DataTableColumn<MailEventLogEntry>> = [
    {
      id: 'at',
      header: '日時',
      width: 'w-44',
      cell: (row) => <span className="tabular text-xs">{formatDateTime(row.occurredAt)}</span>,
    },
    {
      id: 'kind',
      header: 'できごと',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{mailEventLabel(row.kind)}</p>
          <p className="truncate text-xs text-muted-foreground">
            {row.detail ?? MAIL_EVENT_HELP[row.kind] ?? ''}
          </p>
        </div>
      ),
    },
    {
      id: 'sourceIp',
      header: '接続元',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => <span className="font-mono text-xs">{row.sourceIp ?? '—'}</span>,
    },
    {
      id: 'count',
      header: '件数',
      width: 'w-20',
      align: 'right',
      hideBelow: 'md',
      cell: (row) => <span className="tabular text-xs">{formatCount(row.count)}</span>,
    },
    {
      id: 'severity',
      header: '重要度',
      width: 'w-32',
      cell: (row) => (
        <div className="space-y-1">
          <StatusBadge
            status={severityTone(row.severity)}
            label={SEVERITY_LABEL[row.severity]}
            size="sm"
            dot
          />
          {row.blockedLocally ? (
            <Badge variant="muted" size="sm">
              遮断済み
            </Badge>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <HelpNote title="1 通ごとの配送状況をお探しの場合">
        ここに出るのは「ログイン失敗の多発」「送信上限の超過」など、
        サーバーが検知して対処したできごとです。
        特定のメールが届いたかどうかは「メールの配送記録」タブで、
        差出人・宛先から検索してください。
      </HelpNote>

      <Field label="重要度" className="max-w-[12rem]">
        <Select
          value={severity}
          onValueChange={(v) => {
            setSeverity(v);
            page.reset();
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">すべて</SelectItem>
            <SelectItem value="critical">重大のみ</SelectItem>
            <SelectItem value="warning">注意のみ</SelectItem>
            <SelectItem value="info">情報のみ</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.isError ? '履歴を取得できませんでした。' : undefined}
        emptyTitle="記録がありません"
        emptyDescription="問題は検知されていません。"
        emptyAction={
          severity !== 'all' ? (
            <Button
              variant="outline"
              onClick={() => {
                setSeverity('all');
                page.reset();
              }}
            >
              条件をクリア
            </Button>
          ) : undefined
        }
        footer={
          <CursorPagination
            nextCursor={list.data?.nextCursor ?? null}
            canGoPrev={page.canGoPrev}
            onNext={() => page.goNext(list.data?.nextCursor ?? null)}
            onPrev={page.goPrev}
            limit={page.limit}
            onLimitChange={page.setLimit}
            currentCount={rows.length}
            loading={list.isFetching}
          />
        }
      />
    </div>
  );
}

/**
 * 監査ログの `action` → 日本語。
 *
 * ここに出るのは **コントロールパネル（portal）の認証操作だけ**。
 * IMAP / POP3 / Webメールのログインはノード側で記録され、CP には
 * 集計としてしか上がってこないので、接続サービス別の内訳は出せない。
 */
const AUTH_ACTION_LABEL: Record<string, string> = {
  'auth.login': 'ログイン',
  'auth.refresh_reuse_detected': 'セッションの不正な再利用を検知',
  'auth.totp_enabled': '2 段階認証を有効化',
  'auth.totp_disabled': '2 段階認証を無効化',
  'auth.password_changed': 'パスワードを変更',
  'auth.password_reset_requested': 'パスワード再設定を申請',
  'auth.password_reset': 'パスワードを再設定',
};

function AuthLogTab() {
  const page = useCursorPagination(50);
  const [result, setResult] = React.useState('all');

  const list = useQuery({
    queryKey: qk.authLogs({ result, cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<AuthLogEntry>>(`/logs/auth?${params.toString()}`);
    },
  });

  // 成功 / 失敗の絞り込みは API に無いので画面側で行う。
  const rows = (list.data?.items ?? []).filter(
    (r) => result === 'all' || (result === 'success' ? r.success : !r.success),
  );

  const columns: Array<DataTableColumn<AuthLogEntry>> = [
    {
      id: 'at',
      header: '日時',
      width: 'w-44',
      cell: (row) => <span className="tabular text-xs">{formatDateTime(row.occurredAt)}</span>,
    },
    {
      id: 'account',
      header: 'アカウント',
      // 運営者・system の操作は組織ユーザーに解決できない。
      cell: (row) => row.actorEmail ?? '（システム）',
    },
    {
      id: 'action',
      header: '操作',
      width: 'w-56',
      hideBelow: 'md',
      cell: (row) => AUTH_ACTION_LABEL[row.action] ?? row.action,
    },
    {
      id: 'ip',
      header: 'IP アドレス',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => <span className="font-mono text-xs">{row.ip ?? '—'}</span>,
    },
    {
      id: 'result',
      header: '結果',
      width: 'w-28',
      cell: (row) => (
        <StatusBadge
          status={row.success ? 'succeeded' : 'failed'}
          label={row.success ? '成功' : '失敗'}
          size="sm"
          dot
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <HelpNote title="失敗が続いているとき">
        パスワードの間違いが短時間に繰り返されると、その接続元 IP アドレスを一時的に遮断します。
        心当たりのないアクセスが続く場合は、該当アドレスのパスワードを変更してください。
        お客様の事務所などから接続できなくなってしまった場合は、
        <AppLink to="/security" className="mx-1 text-primary underline-offset-4 hover:underline">
          セキュリティ
        </AppLink>
        の画面で遮断されているアドレスを確認し、その場で解除を申請できます。
      </HelpNote>

      {/*
       * **この一覧は `auth.*` だけ。**
       *
       * API（`GET /logs/auth`）が `action like 'auth.%'` で絞っているので、
       * 設定変更の履歴はここには出ない。**出せる場所（「操作の履歴」タブ）を
       * 必ず案内する**。以前ここは「上位プランの書き出し機能で取得できます」と
       * 案内していたが、`GET /logs/audit` にプランゲートは無いので誤りだった。
       */}
      <p className="text-xs text-muted-foreground">
        ここに表示されるのは、コントロールパネルへのログインに関する記録です。
        メールアドレスの作成・削除などの設定変更は「操作の履歴」タブでご確認いただけます。
        まとめて書き出したい場合は
        <AppLink to="/exports" className="mx-1 text-primary underline-offset-4 hover:underline">
          データの書き出し
        </AppLink>
        をご利用ください。
      </p>

      <Field label="結果" className="max-w-[12rem]">
        <Select
          value={result}
          onValueChange={(v) => {
            setResult(v);
            page.reset();
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">すべて</SelectItem>
            <SelectItem value="success">成功のみ</SelectItem>
            <SelectItem value="failure">失敗のみ</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.isError ? '履歴を取得できませんでした。' : undefined}
        emptyTitle="ログインの記録がありません"
        footer={
          <CursorPagination
            nextCursor={list.data?.nextCursor ?? null}
            canGoPrev={page.canGoPrev}
            onNext={() => page.goNext(list.data?.nextCursor ?? null)}
            onPrev={page.goPrev}
            limit={page.limit}
            onLimitChange={page.setLimit}
            currentCount={rows.length}
            loading={list.isFetching}
          />
        }
      />
    </div>
  );
}
